import { DatePicker, FormField } from '@vwfs-bronson/bronson-react';
import { useField, useFormikContext } from 'formik';
import React, { MouseEventHandler } from 'react';
import { InfoIcon } from '../../info-icon';
import { CustomLocale, key } from 'flatpickr/dist/types/locale';
import { getInputErrorMessageComponentAndId, hasError } from '../errors';

export type ValidatedDateProps = {
    name: string;
    label: string;
    ariaLabel?: string;
    onLinkClick?: MouseEventHandler;
    type?: 'text';
    testId?: string;
    id?: string;
    language: string;
    minDate?: string;
    maxDate?: string;
    outputDateFormat: string;
    excludeWeekend?: boolean;
    disableMobile?: boolean;
    tooltip?: string | React.ReactNode;
    value?: string;
    isMandatory?: boolean;
    placeholder?: string;
    onFocus?: () => void;
    customDisableDateConditions?: ((date: Date) => boolean)[];
    disabled?: boolean;
    hint?: string;
    maskedInput?: boolean;
    className?: string;
    required?: boolean;
    labelId?: string;
    requiredText?: string;
};

export const ValidatedDate: React.FC<ValidatedDateProps & React.HTMLAttributes<HTMLInputElement>> = ({
    name,
    language,
    minDate,
    maxDate,
    outputDateFormat,
    excludeWeekend,
    disableMobile,
    label,
    ariaLabel,
    onLinkClick,
    testId,
    id,
    tooltip,
    value,
    isMandatory = false,
    placeholder,
    onFocus,
    customDisableDateConditions = [],
    disabled,
    hint,
    maskedInput = false,
    className,
    required,
    labelId,
    requiredText,
}) => {
    const { setFieldTouched, setFieldValue, getFieldMeta } = useFormikContext();
    const [, meta] = useField({ name, type: 'input' });

    const infoIcon = tooltip ? <InfoIcon text={tooltip} labelId={labelId} /> : null;
    const { errorMessageComponent, errorMessageId } = getInputErrorMessageComponentAndId({
        name,
        id,
        meta,
        testId,
    });

    const disabledDateConditions = [...customDisableDateConditions];
    if (excludeWeekend) disabledDateConditions.push((date: Date) => date.getDay() === 0 || date.getDay() === 6);

    return (
        <FormField
            type="input"
            onClick={onLinkClick}
            labelText={label}
            labelForId={id}
            testId={`${testId}FormField`}
            errorMessage={errorMessageComponent}
            infoIcon={infoIcon}
            required={isMandatory}
            hint={hint}
            className={className}
            requiredText={requiredText}
        >
            <DatePicker
                error={!disabled && hasError(meta)}
                id={id}
                name={name}
                placeholder={placeholder}
                dateFormat={outputDateFormat}
                flatpickrOptions={{
                    disable: disabledDateConditions,
                    locale: language as key | Partial<CustomLocale>,
                    disableMobile: !!disableMobile,
                    errorHandler: (e: unknown) => {
                        console.log(`Flatpickr error: ${e}`);
                    },
                }}
                maxDate={maxDate}
                minDate={minDate}
                onBlur={(): void => {
                    setFieldTouched(name, true, true);
                }}
                onChange={(value): void => {
                    setFieldValue(name, value, getFieldMeta(name).touched);
                }}
                value={value}
                onFocus={onFocus}
                disabled={disabled}
                maskedInput={maskedInput}
                aria-describedby={errorMessageId}
                aria-label={ariaLabel || label}
                required={required}
            />
        </FormField>
    );
};
